import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../services/api/axios";
import { toast } from "react-toastify";
import { Palette, Mail, Phone, Sparkles, Layers } from "lucide-react";
import DataTable from "../../components/DataTable";
import { resolveBuyerUserId } from "../../utils/buyerUserId";
import { ViewButton } from "../../components/Buttons";
import CountdownBadge from "../../components/CountdownBadge";
import AvailabilityTag from "../../components/AvailabilityTag";

const Requests = ({ buyerId, saleData }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [designers, setDesigners] = useState([]);
  // gridRef is retained for parity with the previous implementation in case
  // downstream code relies on it; not used directly in render.
  const gridRef = useRef(null);

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        setLoading(true);

        if (!saleData) {
          setLoading(false);
          return;
        }

        const propertyId = saleData?.propertyData?.propertyId;
        // Prefer the actual user id from sale payload; fallback to buyerId prop only when missing.
        const userId =
          saleData?.user?.userId ||
          saleData?.user?.user_id ||
          saleData?.user?.id ||
          resolveBuyerUserId(buyerId, saleData?.user);

        console.log("[BV Requests] resolved filters", {
          buyerId,
          propertyId,
          userId,
          saleDataUser: saleData?.user,
        });

        if (!propertyId || !userId) {
          console.warn("[BV Requests] missing filter(s), skipping fetch", { propertyId, userId });
          setLoading(false);
          return;
        }

        // Fetch leads filtered by property and user
        const response = await axiosInstance.get("/interior-designers/leads", {
          params: {
            propertyId: propertyId,
            userId: userId,
            page: 1,
            page_size: 1000, // Get all leads for this property/user
          },
        });

        // Handle paginated response
        const payload = response?.data ?? response;
        let leadsArray = [];
        if (Array.isArray(payload)) {
          leadsArray = payload;
        } else if (Array.isArray(payload?.items)) {
          leadsArray = payload.items;
        } else if (Array.isArray(payload?.data?.items)) {
          leadsArray = payload.data.items;
        } else if (Array.isArray(payload?.data)) {
          leadsArray = payload.data;
        }

        console.log("[BV Requests] leads response parsed", {
          responseShape: {
            isArray: Array.isArray(response),
            hasItems: Array.isArray(response?.items),
            hasDataItems: Array.isArray(response?.data?.items),
          },
          parsedLeadsCount: leadsArray.length,
          firstLead: leadsArray[0] || null,
        });

        const fetchIterationStatsForLead = async (idLeadId) => {
          if (!idLeadId) return { count: 0, hasNewRequest: false };
          try {
            // Deliverables live directly on the lead now.
            const designResponse = await axiosInstance.get("/interior-designers/design-deliverables", {
              params: { id_lead_id: parseInt(idLeadId) },
            });

            const iterations = Array.isArray(designResponse)
              ? designResponse
              : Array.isArray(designResponse?.items)
              ? designResponse.items
              : [];

            const totalIterations = iterations.length;
            // "New" while a deliverable is still awaiting review (no buyer feedback yet).
            const hasNewRequest = iterations.some((iteration) => {
              const buyerFeedback = iteration?.buyerFeedback;
              return !buyerFeedback || String(buyerFeedback).trim() === "";
            });

            return { count: totalIterations, hasNewRequest };
          } catch (err) {
            console.warn("Failed to fetch iterations for lead:", idLeadId, err);
            return { count: 0, hasNewRequest: false };
          }
        };

        const getDesignLabel = (featuredIteration) => {
          const iteration =
            featuredIteration?.designIteration ||
            featuredIteration?.design_iteration ||
            null;
          if (!iteration) return null;

          const interactionName =
            iteration?.interactionName ||
            iteration?.interaction_name ||
            iteration?.iterationName ||
            iteration?.iteration_name ||
            iteration?.title ||
            null;
          if (typeof interactionName === "string" && interactionName.trim().length > 0) {
            return interactionName.trim();
          }

          const iterationNumber =
            iteration?.iterationNumber ?? iteration?.iteration_number ?? null;
          const designSubmission =
            iteration?.designSubmission || iteration?.design_submission || null;
          const submissionTitle =
            designSubmission?.title ||
            designSubmission?.name ||
            designSubmission?.designName ||
            null;

          if (submissionTitle && iterationNumber !== null && iterationNumber !== undefined) {
            return `${submissionTitle} - Iteration #${iterationNumber}`;
          }
          if (submissionTitle) return submissionTitle;
          if (iterationNumber !== null && iterationNumber !== undefined) {
            return `Iteration #${iterationNumber}`;
          }

          return "Design Iteration";
        };

        // Group leads by interior designer
        const designerMap = new Map();

        leadsArray.forEach((lead) => {
          const featuredFromLead = lead?.featuredDesignIterations || lead?.featured_design_iterations || [];
          const firstFeatured = featuredFromLead?.[0] || null;
          const firstFeaturedDesigner = firstFeatured?.interiorDesigner || firstFeatured?.interior_designer || null;
          const firstPortfolioDesigner =
            lead?.interiorDesignerPortfolioItems?.[0]?.interiorDesigner ||
            lead?.interior_designer_portfolio_items?.[0]?.interiorDesigner ||
            null;

          const designerId =
            lead?.interiorDesignerId ||
            lead?.interior_designer_id ||
            firstFeatured?.interiorDesignerId ||
            firstFeatured?.interior_designer_id ||
            null;
          const designer = lead?.interiorDesigner || firstPortfolioDesigner || firstFeaturedDesigner || null;

          if (!designerId) return;

          if (!designerMap.has(designerId)) {
            designerMap.set(designerId, {
              interiorDesignerId: designerId,
              name: designer?.name || `Interior Designer #${designerId}`,
              email: designer?.email || "",
              mobile: designer?.mobile || designer?.mobileNoWa || "",
              requestCount: 0, // will represent iteration count
              hasNewRequest: false, // true when a lead has unread iterations
              leads: [],
              isLeadSent: false, // Track if lead has been sent
              nearestAckDeadline: null, // earliest pending acknowledgement deadline across leads
              portfolios: [], // Store portfolio items
              designs: [], // Store design iteration / portfolio labels for table
              isAvailableOnSite: false, // true when any lead marks the designer on-site for this phase
            });
          }

          const designerData = designerMap.get(designerId);
          designerData.leads.push(lead);
          if (lead.isAvailableOnSite || lead.is_available_on_site) designerData.isAvailableOnSite = true;

          // Fill missing designer info from any lead that has richer payload.
          if (designer) {
            designerData.name = designerData.name?.startsWith("Interior Designer #")
              ? (designer?.name || designerData.name)
              : (designerData.name || designer?.name || `Interior Designer #${designerId}`);
            if (!designerData.email) designerData.email = designer?.email || "";
            if (!designerData.mobile) designerData.mobile = designer?.mobile || designer?.mobileNoWa || "";
          }

          // Check if any lead has been acknowledged (lead sent)
          if (lead.isIdAcknowledged || lead.is_id_acknowledged) {
            designerData.isLeadSent = true;
          }

          // Track the nearest still-pending acknowledgement deadline so the list
          // can surface a single countdown per designer.
          {
            const acked = lead.isIdAcknowledged || lead.is_id_acknowledged;
            const ackDeadline = lead.acknowledgementDeadline || lead.acknowledgement_deadline;
            if (!acked && ackDeadline) {
              if (
                !designerData.nearestAckDeadline ||
                new Date(ackDeadline) < new Date(designerData.nearestAckDeadline)
              ) {
                designerData.nearestAckDeadline = ackDeadline;
              }
            }
          }

          // Prefer featured design iterations as the primary design labels.
          if (Array.isArray(featuredFromLead) && featuredFromLead.length > 0) {
            featuredFromLead.forEach((featuredIteration) => {
              const label = getDesignLabel(featuredIteration);
              if (!label) return;
              if (!designerData.designs.includes(label)) {
                designerData.designs.push(label);
              }
            });
          }

          // Collect portfolio items (if present). Featured-only leads still remain visible.
          if (lead.interiorDesignerPortfolioItems && lead.interiorDesignerPortfolioItems.length > 0) {
            lead.interiorDesignerPortfolioItems.forEach((portfolioItem) => {
              // Avoid duplicates by checking if portfolio already exists
              const existingPortfolio = designerData.portfolios.find(
                (p) => p.title === portfolioItem.title
              );
              if (!existingPortfolio) {
                designerData.portfolios.push({
                  title: portfolioItem.title || "Untitled Portfolio",
                  propertyType: portfolioItem.propertyType || "",
                  completedDate: portfolioItem.completedDate || "",
                  overallCost: portfolioItem.overallCost || "",
                });
              }

              // Fallback labels when featured design iterations are absent.
              const fallbackLabel = portfolioItem.title || "Untitled Portfolio";
              if (!designerData.designs.includes(fallbackLabel)) {
                designerData.designs.push(fallbackLabel);
              }
            });
          }
        });

        // Absolute fallback: if no grouped rows but leads exist, group by interiorDesignerId only.
        if (designerMap.size === 0 && leadsArray.length > 0) {
          leadsArray.forEach((lead) => {
            const designerId = lead?.interiorDesignerId || lead?.interior_designer_id;
            if (!designerId) return;
            if (!designerMap.has(designerId)) {
              designerMap.set(designerId, {
                interiorDesignerId: designerId,
                name: `Interior Designer #${designerId}`,
                email: "",
                mobile: "",
                requestCount: 0,
                hasNewRequest: false,
                leads: [],
                isLeadSent: false,
                nearestAckDeadline: null,
                portfolios: [],
                designs: [],
                isAvailableOnSite: false,
              });
            }
            const d = designerMap.get(designerId);
            d.leads.push(lead);
            if (lead.isAvailableOnSite || lead.is_available_on_site) d.isAvailableOnSite = true;
            if (lead.isIdAcknowledged || lead.is_id_acknowledged) d.isLeadSent = true;
            // Track the nearest still-pending acknowledgement deadline across this
            // designer's leads so the list can surface a single countdown.
            const acked = lead.isIdAcknowledged || lead.is_id_acknowledged;
            const ackDeadline = lead.acknowledgementDeadline || lead.acknowledgement_deadline;
            if (!acked && ackDeadline) {
              if (!d.nearestAckDeadline || new Date(ackDeadline) < new Date(d.nearestAckDeadline)) {
                d.nearestAckDeadline = ackDeadline;
              }
            }
          });
        }

        // Compute iteration counts (sum of design iterations across leads) per designer
        const leadsWithDesigner = [];
        designerMap.forEach((d) => {
          d.leads.forEach((lead) => {
            const idLeadId = lead?.interiorDesignerLeadId || lead?.idLeadId || lead?.id_lead_id;
            if (idLeadId) {
              leadsWithDesigner.push({ designerId: d.interiorDesignerId, idLeadId });
            }
          });
        });

        const counts = await Promise.all(
          leadsWithDesigner.map(async ({ designerId, idLeadId }) => {
            const stats = await fetchIterationStatsForLead(idLeadId);
            return {
              designerId,
              count: stats.count,
              hasNewRequest: stats.hasNewRequest,
            };
          })
        );

        counts.forEach(({ designerId, count, hasNewRequest }) => {
          const d = designerMap.get(designerId);
          if (d) {
            d.requestCount += count;
            if (hasNewRequest) d.hasNewRequest = true;
          }
        });

        // Convert map to array and sort by request count (descending)
        const designersArray = Array.from(designerMap.values()).sort(
          (a, b) => b.requestCount - a.requestCount
        );

        console.log("[BV Requests] grouping result", {
          leadsCount: leadsArray.length,
          designerGroups: designerMap.size,
          designersArrayCount: designersArray.length,
          designersArray,
        });

        setDesigners(designersArray);
      } catch (error) {
        console.error("Error fetching leads:", error);
        toast.error("Failed to fetch requests");
      } finally {
        setLoading(false);
      }
    };

    if (saleData) {
      fetchLeads();
    }
  }, [saleData, buyerId]);

  const handleViewRequests = useCallback(
    (designer) => {
      // Navigate to request details page with designer ID and buyer ID
      // Pass lead data and saleData via state to avoid refetching
      navigate(
        `/bv-user/buyer/${buyerId}/requests/${designer.interiorDesignerId}`,
        {
          state: {
            leads: designer.leads,
            saleData: saleData,
            designer: {
              interiorDesignerId: designer.interiorDesignerId,
              name: designer.name,
              email: designer.email,
              mobile: designer.mobile,
            },
          },
        }
      );
    },
    [navigate, buyerId, saleData]
  );

  // Aggregate counts for the section subline (computed once per designers update).
  const summary = useMemo(() => {
    const total = designers.length;
    const iterations = designers.reduce((sum, d) => sum + (d.requestCount || 0), 0);
    const withNew = designers.filter((d) => d.hasNewRequest).length;
    return { total, iterations, withNew };
  }, [designers]);

  // Column definitions for designers ag-grid table
  const columns = useMemo(
    () => [
      {
        key: "name",
        header: "Designer",
        width: 220,
        render: (r) => (
          <div className="min-w-0">
            <p
              className="text-sm font-semibold text-slate-900 truncate"
              title={r.name}
            >
              {r.name || "Unknown designer"}
            </p>
            {r.isLeadSent && (
              <p className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700">
                <Sparkles size={10} />
                Lead acknowledged
              </p>
            )}
            {r.isAvailableOnSite && (
              <div className="mt-1">
                <AvailabilityTag isAvailableOnSite size="xs" hideWhenRemote />
              </div>
            )}
          </div>
        ),
      },
      {
        key: "designs",
        header: "Design",
        width: 220,
        render: (r) => {
          const designs = r.designs || [];
          if (designs.length === 0)
            return <span className="text-xs text-slate-400">—</span>;
          const first = designs[0];
          return (
            <div className="min-w-0">
              <p className="text-sm text-slate-700 truncate" title={first}>
                {first}
              </p>
              {designs.length > 1 && (
                <p className="text-[11px] text-slate-500 mt-0.5">
                  +{designs.length - 1} more
                </p>
              )}
            </div>
          );
        },
      },
      {
        key: "email",
        header: "Email",
        width: 220,
        render: (r) => (
          <span
            className="inline-flex items-center gap-1.5 text-sm text-slate-700 truncate max-w-full"
            title={r.email}
          >
            {r.email ? (
              <>
                <Mail size={12} className="text-slate-400 shrink-0" />
                <span className="truncate">{r.email}</span>
              </>
            ) : (
              "—"
            )}
          </span>
        ),
      },
      {
        key: "mobile",
        header: "Mobile",
        width: 150,
        render: (r) => (
          <span className="inline-flex items-center gap-1.5 text-sm text-slate-700 tabular-nums">
            {r.mobile ? (
              <>
                <Phone size={12} className="text-slate-400 shrink-0" />
                {r.mobile}
              </>
            ) : (
              "—"
            )}
          </span>
        ),
      },
      {
        key: "count",
        header: "Iterations",
        width: 110,
        render: (r) => (
          <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 border border-slate-200 px-2 py-0.5 text-xs font-semibold text-slate-700 tabular-nums">
            <Layers size={11} className="text-slate-400" />
            {r.requestCount || 0}
          </span>
        ),
      },
      {
        key: "ackDeadline",
        header: "Acknowledgement",
        width: 200,
        render: (r) =>
          r.nearestAckDeadline ? (
            <CountdownBadge deadline={r.nearestAckDeadline} label="Due in" expiredLabel="Overdue" />
          ) : (
            <span className="text-xs text-slate-400">—</span>
          ),
      },
      {
        key: "actions",
        header: "View",
        width: 160,
        align: "center",
        render: (r) => (
          <div className="flex items-center justify-center gap-2">
            {r.hasNewRequest && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold bg-flamingo-50 text-flamingo-700 border border-flamingo-200">
                + NEW
              </span>
            )}
            <ViewButton
              onClick={(e) => {
                e.stopPropagation();
                handleViewRequests(r);
              }}
            />
          </div>
        ),
      },
    ],
    [handleViewRequests]
  );

  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
      <div className="flex flex-col gap-3 border-b border-slate-100 px-6 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-8">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-flamingo-50 text-flamingo-600">
            <Palette size={18} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Design requests</h2>
            <p className="mt-0.5 text-sm text-slate-500">
              {loading
                ? "Loading interior designer requests…"
                : summary.total === 0
                ? "No interior designers have requests for this buyer yet."
                : `${summary.total} ${summary.total === 1 ? "designer" : "designers"} · ${summary.iterations} ${summary.iterations === 1 ? "iteration" : "iterations"}`}
            </p>
          </div>
        </div>
        {!loading && summary.withNew > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-flamingo-50 px-2.5 py-1 text-xs font-semibold text-flamingo-700 border border-flamingo-200">
            <Sparkles size={12} />
            {summary.withNew} with new updates
          </span>
        )}
      </div>

      <DataTable
        columns={columns}
        data={designers}
        loading={loading}
        minWidth={1050}
        onRowClick={(r) => handleViewRequests(r)}
        rowKey={(r, i) => r?.id ?? r?.interiorDesignerId ?? i}
        emptyState={
          <div className="px-6 py-16 text-center">
            <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
              <Palette className="text-slate-400" size={22} />
            </div>
            <p className="mt-4 text-sm font-semibold text-slate-700">
              No requests found
            </p>
            <p className="mt-1 text-xs text-slate-500">
              No interior designers have requests for this buyer yet.
            </p>
          </div>
        }
      />
    </div>
  );
};

export default Requests;
